import React, { useEffect } from 'react';
import { Box, Typography, Stack, Switch, Chip, Paper, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Button, Container, TextField } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useQuery } from '@tanstack/react-query';
import {
    fetchPoliticalPodcastsCount,
} from '../api/podcasts';
import PoliticsIcon from '@mui/icons-material/AccountBalance';
import PodcastIcon from '@mui/icons-material/Podcasts';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { PoliticalStatsVisualization } from '../components/elections/PoliticalStatsVisualization';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { convertToCSV, downloadCSV, createDateRangeFilename } from '../utils/csvUtils';
import apiService from '../services/api';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

// Helper function to create Chilean date range
// We'll create UTC dates that represent the Chilean timezone boundaries
const createChileanRange = (fromDate: string, toDate: string) => {
    // Chile is UTC-4 during this period (winter time)
    // So 00:00 Chilean time = 04:00 UTC same day
    // And 23:59 Chilean time = 03:59 UTC next day

    const fromUTC = new Date(`${fromDate}T04:00:00.000Z`); // 00:00 Chilean = 04:00 UTC

    // For the end date, we need to add one day to get 23:59 Chilean time
    // Use UTC date manipulation to avoid timezone issues
    const endDateUTC = new Date(`${toDate}T00:00:00.000Z`);
    endDateUTC.setUTCDate(endDateUTC.getUTCDate() + 1);
    const endDateStr = endDateUTC.toISOString().split('T')[0];
    const toUTC = new Date(`${endDateStr}T02:59:59.999Z`); // 23:59 Chilean summer time = 02:59 UTC next day

    const result = {
        from: fromUTC.toISOString(),
        to: toUTC.toISOString()
    };

    return result;
};

// Fixed analysis ranges - now using Chilean timezone
const RANGES = {
    total: {
        label: 'Total del periodo',
        defaultView: 'weekly' as const,
        ...createChileanRange('2025-06-29', '2025-12-14'),
    },
    pre1ra: {
        label: 'Pre 1ra Vuelta (hasta Nov 15)',
        defaultView: 'weekly' as const,
        ...createChileanRange('2025-06-29', '2025-11-15'),
    },
    entre1y2: {
        label: 'Entre 1ra y 2da Vuelta',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-11-17', '2025-12-13'),
        restrictedCandidates: ['José Antonio Kast', 'Jeannette Jara'] as string[],
    },
    w0: {
        label: 'Semana del 29 de Junio',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-06-29', '2025-07-06'),
    },
    w1: {
        label: 'Semana del 7 de Julio',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-07-07', '2025-07-13'),
    },
    w2: {
        label: 'Semana del 14 de Julio',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-07-14', '2025-07-20'),
    },
    w3: {
        label: 'Semana del 21 de Julio',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-07-21', '2025-07-27'),
    },
    w4: {
        label: 'Semana del 28 de Julio',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-07-28', '2025-08-03'),
    },
    w5: {
        label: 'Semana del 4 de Agosto',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-08-04', '2025-08-10'),
    },
    w6: {
        label: 'Semana del 11 de Agosto',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-08-11', '2025-08-17'),
    },
    w7: {
        label: 'Semana del 18 de Agosto',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-08-18', '2025-08-24'),
    },
    w8: {
        label: 'Semana del 25 de Agosto',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-08-25', '2025-08-31'),
    },
    w9: {
        label: 'Semana del 1 de Septiembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-09-01', '2025-09-07'),
    },
    w10: {
        label: 'Semana del 8 de Septiembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-09-08', '2025-09-14'),
    },
    w11: {
        label: 'Semana del 15 de Septiembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-09-15', '2025-09-21'),
    },
    w12: {
        label: 'Semana del 22 de Septiembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-09-22', '2025-09-28'),
    },
    w13: {
        label: 'Semana del 29 de Septiembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-09-29', '2025-10-05'),
    },
    w14: {
        label: 'Semana del 6 de Octubre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-10-06', '2025-10-12'),
    },
    w15: {
        label: 'Semana del 13 de Octubre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-10-13', '2025-10-19'),
    },
    w16: {
        label: 'Semana del 20 de Octubre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-10-20', '2025-10-26'),
    },
    w17: {
        label: 'Semana del 27 de Octubre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-10-27', '2025-11-02'),
    },
    w18: {
        label: 'Semana del 3 de Noviembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-11-03', '2025-11-09'),
    },
    w19: {
        label: 'Semana del 10 de Noviembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-11-10', '2025-11-16'),
    },
    w20: {
        label: 'Semana del 17 de Noviembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-11-17', '2025-11-23'),
    },
    w21: {
        label: 'Semana del 24 de Noviembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-11-24', '2025-11-30'),
    },
    w22: {
        label: 'Semana del 1 de Diciembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-12-01', '2025-12-07'),
    },
    w23: {
        label: 'Semana del 8 de Diciembre',
        defaultView: 'daily' as const,
        ...createChileanRange('2025-12-08', '2025-12-14'),
    },
} as const;

export function parseDuration(duration: string | number | null | undefined): number {
    if (!duration) return 0;

    // Try converting to number first (handles string numbers like "3600")
    const numericDuration = Number(duration);
    if (!isNaN(numericDuration) && numericDuration > 0) {
        return numericDuration;
    }

    // Convert to string for pattern matching
    const durationStr = duration.toString();

    // Try HH:MM:SS format (standard format)
    const timeMatches = durationStr.match(/(?:(\d+):)?(\d+):(\d+)/);
    if (timeMatches) {
        const [, hours, minutes, seconds] = timeMatches;
        return (
            parseInt(hours || '0') * 3600 + parseInt(minutes || '0') * 60 + parseInt(seconds || '0')
        );
    }

    // Try ISO format (PT1H1M57S)
    const isoMatches = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (isoMatches) {
        const hours = parseInt(isoMatches[1] || '0');
        const minutes = parseInt(isoMatches[2] || '0');
        const seconds = parseInt(isoMatches[3] || '0');
        return hours * 3600 + minutes * 60 + seconds;
    }

    // Try minutes format (e.g., "45m", "45 min")
    const minutesMatch = durationStr.match(/^(\d+)\s*(?:m|min|minutes?)$/i);
    if (minutesMatch && minutesMatch[1]) {
        return parseInt(minutesMatch[1]) * 60;
    }

    // Try hours format (e.g., "2h", "2 hours")
    const hoursMatch = durationStr.match(/^(\d+)\s*(?:h|hr|hours?)$/i);
    if (hoursMatch && hoursMatch[1]) {
        return parseInt(hoursMatch[1]) * 3600;
    }

    return 0;
}

// Compact Timeline Selector Component
const TimelineSelector = ({
    selectedRangeKeys,
    onRangeChange,
    customDateRange,
    onCustomDateChange,
    isCustomSelected,
    onCustomToggle
}: {
    selectedRangeKeys: (keyof typeof RANGES)[];
    onRangeChange: (keys: (keyof typeof RANGES)[]) => void;
    customDateRange: { from: string; to: string };
    onCustomDateChange: (field: 'from' | 'to', value: string) => void;
    isCustomSelected: boolean;
    onCustomToggle: () => void;
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t, i18n } = useTranslation('elecciones');
    const dateLocale = i18n.language.startsWith('en') ? 'en-GB' : 'es-CL';
    const formatShort = (value: string) =>
        new Date(value).toLocaleDateString(dateLocale, {
            timeZone: 'America/Santiago',
            day: 'numeric',
            month: 'short',
        });

    const weeklyRanges = Object.entries(RANGES).filter(([key]) => key.startsWith('w')) as [keyof typeof RANGES, typeof RANGES[keyof typeof RANGES]][];

    const handleWeekToggle = (key: keyof typeof RANGES) => {
        if (key === 'total') {
            // Total is exclusive - clear all other selections
            onRangeChange(['total']);
        } else {
            // Remove 'total' if it's selected
            const filtered = selectedRangeKeys.filter(k => k !== 'total');

            if (filtered.includes(key)) {
                // Deselect this week
                const newSelection = filtered.filter(k => k !== key);
                // If nothing left, default to total
                onRangeChange(newSelection.length > 0 ? newSelection : ['total']);
            } else {
                // Add this week to selection
                onRangeChange([...filtered, key]);
            }
        }
    };

    const isTotalSelected = selectedRangeKeys.includes('total') && !isCustomSelected;

    return (
        <Box sx={{ width: '100%' }}>
            {/* Total Period Option - Compact */}
            <Paper
                elevation={isTotalSelected ? 1 : 0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    cursor: 'pointer',
                    border: `2px solid ${isTotalSelected
                        ? theme.palette.primary.main
                        : theme.palette.divider}`,
                    backgroundColor: isTotalSelected
                        ? theme.palette.primary.main + '08'
                        : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        backgroundColor: isTotalSelected
                            ? theme.palette.primary.main + '12'
                            : theme.palette.action.hover,
                        border: `2px solid ${isTotalSelected
                            ? theme.palette.primary.main
                            : theme.palette.divider}`,
                    }
                }}
                onClick={() => handleWeekToggle('total')}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                            {t('period.total')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('period.totalDates')}
                        </Typography>
                    </Box>
                    {isTotalSelected && (
                        <Chip
                            label="✓"
                            color="primary"
                            size="small"
                            variant="filled"
                            sx={{ minWidth: 'auto', width: 24, height: 24, fontSize: '0.7rem' }}
                        />
                    )}
                </Box>
            </Paper>

            {/* Weekly Options - Compact Grid */}
            <Box>
                {selectedRangeKeys.length > 1 && !isTotalSelected && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('period.weeksSelected', { count: selectedRangeKeys.length })}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile
                            ? 'repeat(2, 1fr)'
                            : 'repeat(7, 1fr)',
                        gap: 1,
                    }}
                >
                    {weeklyRanges.map(([key, range], index) => {
                        const isSelected = selectedRangeKeys.includes(key);
                        const weekNumber = index + 1;

                        return (
                            <Paper
                                key={key}
                                elevation={isSelected ? 1 : 0}
                                sx={{
                                    p: 1.25,
                                    cursor: 'pointer',
                                    border: `2px solid ${isSelected
                                        ? theme.palette.primary.main
                                        : theme.palette.divider}`,
                                    backgroundColor: isSelected
                                        ? theme.palette.primary.main + '08'
                                        : 'transparent',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        backgroundColor: isSelected
                                            ? theme.palette.primary.main + '12'
                                            : theme.palette.action.hover,
                                        border: `2px solid ${isSelected
                                            ? theme.palette.primary.main
                                            : theme.palette.divider}`,
                                    }
                                }}
                                onClick={() => handleWeekToggle(key)}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                                            {t('period.week', { number: weekNumber })}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {formatShort(range.from)} – {formatShort(range.to)}
                                        </Typography>
                                    </Box>
                                    {isSelected && !isCustomSelected && (
                                        <Chip
                                            label="✓"
                                            color="primary"
                                            size="small"
                                            variant="filled"
                                            sx={{ minWidth: 'auto', width: 20, height: 20, fontSize: '0.6rem', ml: 0.5 }}
                                        />
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Box>

            {/* Custom Date Range Option */}
            <Paper
                elevation={isCustomSelected ? 1 : 0}
                sx={{
                    p: 1.5,
                    mt: 2,
                    border: `2px solid ${isCustomSelected
                        ? theme.palette.secondary.main
                        : theme.palette.divider}`,
                    backgroundColor: isCustomSelected
                        ? theme.palette.secondary.main + '08'
                        : 'transparent',
                    transition: 'all 0.15s ease',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        mb: isCustomSelected ? 1.5 : 0
                    }}
                    onClick={onCustomToggle}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DateRangeIcon fontSize="small" color={isCustomSelected ? 'secondary' : 'action'} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {t('period.custom')}
                        </Typography>
                    </Box>
                    {isCustomSelected && (
                        <Chip
                            label="✓"
                            color="secondary"
                            size="small"
                            variant="filled"
                            sx={{ minWidth: 'auto', width: 24, height: 24, fontSize: '0.7rem' }}
                        />
                    )}
                </Box>
                {isCustomSelected && (
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center'
                    }}>
                        <TextField
                            label={t('period.from')}
                            type="date"
                            size="small"
                            value={customDateRange.from}
                            onChange={(e) => onCustomDateChange('from', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: '2025-06-29',
                                max: '2025-12-14'
                            }}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <TextField
                            label={t('period.to')}
                            type="date"
                            size="small"
                            value={customDateRange.to}
                            onChange={(e) => onCustomDateChange('to', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: '2025-06-29',
                                max: '2025-12-14'
                            }}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export function Elecciones() {
    const { t } = useTranslation('elecciones');
    const [selectedRangeKeys, setSelectedRangeKeys] = React.useState<(keyof typeof RANGES)[]>(['total']);
    const [includeRadio, setIncludeRadio] = React.useState<boolean>(true);
    const [infoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false);
    const [isCustomSelected, setIsCustomSelected] = React.useState<boolean>(false);
    const [customDateRange, setCustomDateRange] = React.useState<{ from: string; to: string }>({
        from: '2025-06-29',
        to: '2025-12-14'
    });

    const handleCustomDateChange = (field: 'from' | 'to', value: string) => {
        setCustomDateRange(prev => ({ ...prev, [field]: value }));
    };

    const handleCustomToggle = () => {
        setIsCustomSelected(prev => !prev);
    };

    const handleRangeChange = (keys: (keyof typeof RANGES)[]) => {
        setIsCustomSelected(false); // Deselect custom when selecting predefined ranges
        setSelectedRangeKeys(keys);
    };

    // Compute the combined range from selected weeks or custom range
    const selectedRange = React.useMemo(() => {
        // Custom date range takes priority
        if (isCustomSelected && customDateRange.from && customDateRange.to) {
            const customRange = createChileanRange(customDateRange.from, customDateRange.to);
            // Determine default view based on range length
            const fromDate = new Date(customDateRange.from);
            const toDate = new Date(customDateRange.to);
            const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));

            return {
                label: t('period.custom'),
                defaultView: daysDiff <= 14 ? 'daily' as const : 'weekly' as const,
                from: customRange.from,
                to: customRange.to,
            };
        }

        if (selectedRangeKeys.includes('total')) {
            return RANGES.total;
        }

        if (selectedRangeKeys.length === 1) {
            return RANGES[selectedRangeKeys[0]];
        }

        // Combine multiple weeks
        const ranges = selectedRangeKeys.map(key => RANGES[key]);
        const fromDates = ranges.map(r => new Date(r.from));
        const toDates = ranges.map(r => new Date(r.to));

        const earliestFrom = new Date(Math.min(...fromDates.map(d => d.getTime())));
        const latestTo = new Date(Math.max(...toDates.map(d => d.getTime())));

        return {
            label: t('period.weeksSelected', { count: selectedRangeKeys.length }),
            defaultView: 'weekly' as const,
            from: earliestFrom.toISOString(),
            to: latestTo.toISOString(),
        };
    }, [selectedRangeKeys, isCustomSelected, customDateRange, t]);

    // Track page visit when component mounts
    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_ELECTIONS_DATA, {
            page_url: window.location.pathname + window.location.search,
            page_path: window.location.pathname,
            page_search: window.location.search,
            page_hash: window.location.hash,
        });
    }, []); // Empty dependency array means this runs once on mount

    // Fetch political podcasts count
    const { data: politicalPodcastsCount, isLoading: isLoadingPodcastsCount } = useQuery<{
        count: number;
        episodeCount: number;
        podcastCount: number;
        mentionsCount: number;
        totalHours: number;
    }>({
        queryKey: ['political-podcasts-count', selectedRange.from, selectedRange.to, includeRadio],
        queryFn: async () => {
            return fetchPoliticalPodcastsCount({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
            });
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        gcTime: 4 * 60 * 60 * 1000, // 4 hours
    });

    // CSV Download function for content data
    const downloadContentCSV = async () => {
        try {
            // Fetch both datasets in parallel
            const [podcastsData, topicsData] = await Promise.all([
                apiService.fetchPoliticalPodcasts({ includeRadio }),
                apiService.fetchPoliticalTopics()
            ]);

            // Create podcasts CSV data
            const podcastsCsvData = podcastsData.map(podcast => ({
                [t('csv.title')]: podcast.title,
                URL: podcast.url,
                [t('csv.broadcast')]: podcast.isRadioTv ? t('csv.yes') : t('csv.no'),
            }));

            const topicsCsvData = topicsData.map(topic => ({
                [t('fields.topic')]: topic.name,
                [t('csv.keywords')]: topic.aliases.join(', '),
            }));

            // Generate CSV content for both files
            const podcastsCsv = convertToCSV(podcastsCsvData);
            const topicsCsv = convertToCSV(topicsCsvData);

            // Create filenames with filter suffix
            const filterSuffix = includeRadio ? '_con_radio_tv' : '_solo_podcasts';
            const podcastsFilename = createDateRangeFilename(`podcasts_politicos${filterSuffix}`, selectedRange.from, selectedRange.to);
            const topicsFilename = createDateRangeFilename('temas_politicos', selectedRange.from, selectedRange.to);

            // Download first file
            downloadCSV(podcastsCsv, podcastsFilename);

            // Wait a bit and download second file
            await new Promise(resolve => setTimeout(resolve, 500));
            downloadCSV(topicsCsv, topicsFilename);

        } catch (error) {
            console.error('Error downloading content CSV:', error);
        }
    };

    // CSV Download function for candidate-topic data per week
    const downloadCandidateTopicsCSV = async () => {
        try {
            const data = await apiService.fetchCandidateTopicsWeekly({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
            });

            // Transform data for CSV export
            const csvData = data.map(item => ({
                [t('csv.week')]: item.weekStart,
                [t('fields.candidate')]: item.candidate,
                [t('fields.topic')]: item.topic,
                [t('csv.mentions')]: item.mentionCount,
                [t('csv.duration')]: Math.round(item.totalDuration),
                [t('csv.episodes')]: item.episodeCount,
            }));

            const csv = convertToCSV(csvData);
            const filterSuffix = includeRadio ? '_con_radio_tv' : '_solo_podcasts';
            const filename = createDateRangeFilename(`candidatos_temas_semanal${filterSuffix}`, selectedRange.from, selectedRange.to);
            downloadCSV(csv, filename);

        } catch (error) {
            console.error('Error downloading candidate-topics CSV:', error);
        }
    };

    return (
        <>
            {/* Simple Navbar */}
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    zIndex: 1100,
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 2, md: 4 } }}>
                        <Typography variant="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 700, color: 'primary.main' }}>
                            Podscope
                        </Typography>
                    </Box>
                </Container>
            </Box>

            <Container
                maxWidth="lg"
                sx={{
                    minHeight: '100vh',
                    py: { xs: 2, md: 4 },
                    pt: { xs: 10, md: 12, lg: 14 } // Add top padding to account for fixed navbar
                }}
            >
                <Stack spacing={5}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <Typography variant="h1" component="h1">
                            {t('title')}
                        </Typography>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h2">
                                {t('period.label')}
                            </Typography>

                        </Box>

                        <TimelineSelector
                            selectedRangeKeys={selectedRangeKeys}
                            onRangeChange={handleRangeChange}
                            customDateRange={customDateRange}
                            onCustomDateChange={handleCustomDateChange}
                            isCustomSelected={isCustomSelected}
                            onCustomToggle={handleCustomToggle}
                        />
                    </Box>

                    {/* Stats Section */}
                    <Box>
                        <Stack spacing={3} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h2">
                                    {t('content.title')}
                                </Typography>
                                <CsvDownloadButton
                                    onClick={downloadContentCSV}
                                    tooltip={t('content.downloadContent')}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Stack
                                    gap={3}
                                    direction="row"
                                    alignItems="flex-end"
                                    sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap' }}
                                >
                                    <Stack gap={1} direction="row" alignItems="center">
                                        <PodcastIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Typography variant="h3" color="primary.main">
                                            {isLoadingPodcastsCount
                                                ? '-'
                                                : politicalPodcastsCount?.podcastCount?.toLocaleString() || 0}
                                        </Typography>
                                        <Typography variant="h5">
                                            {includeRadio ? t('content.podcastsAndBroadcast') : t('content.podcasts')}
                                        </Typography>
                                    </Stack>
                                    <Stack gap={1} direction="row" alignItems="center">
                                        <PoliticsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Typography variant="h3" color="primary.main">
                                            {isLoadingPodcastsCount ? '-' : politicalPodcastsCount?.episodeCount}
                                        </Typography>
                                        <Typography variant="h5">{t('content.episodes')}</Typography>
                                    </Stack>
                                    <Stack gap={1} direction="row" alignItems="center">
                                        <RecordVoiceOverIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Typography variant="h3" color="primary.main">
                                            {isLoadingPodcastsCount
                                                ? '-'
                                                : politicalPodcastsCount?.mentionsCount?.toLocaleString() || 0}
                                        </Typography>
                                        <Typography variant="h5">{t('content.detectedMentions')}</Typography>
                                    </Stack>
                                    <Stack gap={1} direction="row" alignItems="center">
                                        <AccessTimeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Typography variant="h3" color="primary.main">
                                            {isLoadingPodcastsCount ? '-' : politicalPodcastsCount?.totalHours || 0}
                                        </Typography>
                                        <Typography variant="h5">{t('content.hours')}</Typography>
                                    </Stack>
                                </Stack>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Switch
                                        checked={includeRadio}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setIncludeRadio(e.target.checked)
                                        }
                                        size="small"
                                        color="primary"
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        {t('content.includeBroadcast')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>


                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <Typography variant="h2">
                                {t('mentions.title')}
                            </Typography>
                            <Box
                                onClick={() => setInfoDialogOpen(true)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        '& .info-text': {
                                            textDecoration: 'underline'
                                        }
                                    }
                                }}
                            >
                                <Typography variant="subtitle1" className="info-text">
                                    (<InfoIcon sx={{ fontSize: 14, color: 'text.primary', mr: 0.5, mb: 0.25 }} />
                                    {t('mentions.what')})
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            <CsvDownloadButton
                                onClick={downloadCandidateTopicsCSV}
                                tooltip={t('content.downloadWeekly')}
                            />
                        </Stack>

                        {/* Political Statistics */}
                        <Box>
                            <PoliticalStatsVisualization
                                selectedRange={selectedRange}
                                includeRadio={includeRadio}
                            />
                        </Box>
                    </Box>
                </Stack>

                {/* Info Dialog */}
                <Dialog
                    open={infoDialogOpen}
                    onClose={() => setInfoDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            px: 1,
                            py: 1,
                        }
                    }}
                >
                    <DialogTitle>
                        <Typography variant="h2">
                            {t('dialog.title')}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ pt: 2 }}>
                            <Box>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    <strong>• {t('dialog.candidate')}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    <strong>• {t('dialog.topic')}</strong>
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="h3" sx={{ mb: 2 }}>
                                    {t('dialog.quality')}
                                </Typography>

                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>• {t('dialog.precision')}</strong>
                                </Typography>
                                <Box sx={{ ml: 2, mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        • {t('dialog.candidatesExact')}
                                    </Typography>
                                    <Typography variant="body2">
                                        • {t('dialog.topicsExact')}
                                    </Typography>
                                </Box>

                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>• {t('dialog.dedupe')}</strong>
                                </Typography>

                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    <strong>• {t('dialog.merge')}</strong>
                                </Typography>

                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {t('dialog.result')}
                                </Typography>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setInfoDialogOpen(false)} color="primary" variant="contained">
                            {t('dialog.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    );
}

export default Elecciones;
